import { ancestorChain } from "../lib/assetPath"
import * as React from "react"
import { useNavigate, useParams } from "@tanstack/react-router"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { AppIcon } from './ui/icon'
import { mdiAccount, mdiAccountGroup, mdiAutoFix, mdiBriefcase, mdiBugOutline, mdiCalculator, mdiCog, mdiDownload, mdiEmailOutline, mdiKey, mdiMagnify, mdiScaleBalance, mdiShape, mdiShieldCheckOutline, mdiSourceBranch, mdiSwapHorizontal, mdiTag, mdiTicketOutline, mdiUpload, mdiSync } from '@mdi/js'
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "./ui/command"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { useAuth } from "../contexts/AuthContext"
import { useApi } from "../api/hooks"
import { tagKeys, fetchTagsList } from "../api/tags"
import type { components } from "../api/schema"
import { formatDisplayNumber } from "../lib/format"
import { useLocale } from "../contexts/LocaleContext"
import { getLocalizedName } from "../lib/localization"
import { SECTION_ICONS } from '../lib/section-icons'

// macOS uses ⌘, every other desktop platform uses Ctrl — same detection as
// FeedbackButton's shortcut hint (front/src/components/feedback/FeedbackButton.tsx).
function isMac(): boolean {
  if (typeof navigator === "undefined") return false
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform)
}

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type CommandCategory = 'navigation' | 'assets' | 'documents' | 'settings'
type SearchResult = components["schemas"]["SearchResult"]

interface NavigationItem {
  id: string
  titleKey: string
  descriptionKey?: string
  /** An MDI path from `@mdi/js`. */
  icon: string
  category: CommandCategory
  route: string
  shortcut?: string
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  // Get all hooks first, before any try-catch or conditional logic
  const { orgCode: authOrg, user } = useAuth()
  const { locale } = useLocale()
  const { request, loading } = useApi()
  const [searchQuery, setSearchQuery] = React.useState("")
  const [searchResults, setSearchResults] = React.useState<SearchResult[]>([])
  const [assetPaths, setAssetPaths] = React.useState<Record<string, string>>({})
  const [debouncedQuery, setDebouncedQuery] = React.useState("")

  // Try to get the org from the current route, fall back to a default
  // Use strict: false to avoid throwing errors during navigation
  const routeParams = useParams({ strict: false }) as { org?: string } | undefined

  // Use the organization from the current route if available,
  // otherwise fall back to the authenticated user's organization
  const currentOrg = routeParams?.org || authOrg

  // Navigation items
  const navigationItems: NavigationItem[] = React.useMemo(() => {
    const items: NavigationItem[] = [
      {
        id: "organizations",
        titleKey: "command_palette.organizations",
        descriptionKey: "command_palette.organizations_desc",
        icon: SECTION_ICONS.assets,
        category: "navigation",
        route: `/organizations`,
      }
    ]

    // Only add organization-specific items if we have a current organization
    if (currentOrg) {
      items.push(
        {
          id: "search",
          titleKey: "command_palette.search",
          descriptionKey: "command_palette.search_desc",
          icon: mdiMagnify,
          category: "navigation",
          route: `/${currentOrg}/search`,
        },
        {
          id: "assets",
          titleKey: "command_palette.assets",
          descriptionKey: "command_palette.assets_desc",
          icon: SECTION_ICONS.assets,
          category: "navigation",
          route: `/${currentOrg}/assets`,
        },
        {
          id: "asset-types",
          titleKey: "command_palette.asset_types",
          descriptionKey: "command_palette.asset_types_desc",
          icon: mdiShape,
          category: "settings",
          route: `/${currentOrg}/asset-types`,
        },
        {
          id: "documents",
          titleKey: "command_palette.documents",
          descriptionKey: "command_palette.documents_desc",
          icon: SECTION_ICONS.documents,
          category: "navigation",
          route: `/${currentOrg}/documents`,
        },
        {
          id: "contracts",
          titleKey: "command_palette.contracts",
          descriptionKey: "command_palette.contracts_desc",
          icon: SECTION_ICONS.contracts,
          category: "navigation",
          route: `/${currentOrg}/contracts`,
        },
        {
          id: "steering",
          titleKey: "command_palette.steering",
          descriptionKey: "command_palette.steering_desc",
          icon: mdiTicketOutline,
          category: "navigation",
          route: `/${currentOrg}/steering`,
        },
        {
          id: "conversations",
          titleKey: "command_palette.conversations",
          descriptionKey: "command_palette.conversations_desc",
          icon: SECTION_ICONS.conversations,
          category: "navigation",
          route: `/${currentOrg}/conversations`,
        },
        {
          id: "intake-addresses",
          titleKey: "command_palette.intake_addresses",
          descriptionKey: "command_palette.intake_addresses_desc",
          icon: mdiEmailOutline,
          category: "settings",
          route: `/${currentOrg}/conversations/intake-addresses`,
        },
        {
          id: "contacts",
          titleKey: "command_palette.contacts",
          descriptionKey: "command_palette.contacts_desc",
          icon: mdiAccountGroup,
          category: "navigation",
          route: `/${currentOrg}/users`,
        },
        {
          id: "profile",
          titleKey: "command_palette.profile",
          descriptionKey: "command_palette.profile_desc",
          icon: mdiAccount,
          category: "navigation",
          route: `/${currentOrg}/users/me/settings`,
        },
        {
          id: "sessions",
          titleKey: "command_palette.sessions",
          descriptionKey: "command_palette.sessions_desc",
          icon: mdiKey,
          category: "navigation",
          route: `/${currentOrg}/user/me/security`,
        },
        {
          id: "pat",
          titleKey: "command_palette.pat",
          descriptionKey: "command_palette.pat_desc",
          icon: mdiKey,
          category: "navigation",
          route: `/${currentOrg}/user/me/pat`,
        },
        {
          id: "email-ingestion",
          titleKey: "command_palette.email_ingestion",
          descriptionKey: "command_palette.email_ingestion_desc",
          icon: mdiKey,
          category: "navigation",
          route: `/${currentOrg}/user/me/email-ingestion`,
        },
        {
          id: "jobs",
          titleKey: "command_palette.jobs",
          descriptionKey: "command_palette.jobs_desc",
          icon: mdiBriefcase,
          category: "navigation",
          route: `/${currentOrg}/jobs`,
        },
        {
          id: "settings",
          titleKey: "command_palette.org_settings",
          descriptionKey: "command_palette.org_settings_desc",
          icon: mdiCog,
          category: "settings",
          route: `/${currentOrg}/settings`,
        },
        {
          id: "properties",
          titleKey: "command_palette.properties",
          descriptionKey: "command_palette.properties_desc",
          icon: mdiCog,
          category: "settings",
          route: `/${currentOrg}/properties`,
        },
        {
          id: "document-types",
          titleKey: "command_palette.document_types",
          descriptionKey: "command_palette.document_types_desc",
          icon: SECTION_ICONS.documents,
          category: "settings",
          route: `/${currentOrg}/document-types`,
        },
        {
          id: "contract-types",
          titleKey: "command_palette.contract_types",
          descriptionKey: "command_palette.contract_types_desc",
          icon: SECTION_ICONS.documents,
          category: "settings",
          route: `/${currentOrg}/contract-types`,
        },
        {
          id: "contract-stakeholder-types",
          titleKey: "command_palette.contract_stakeholder_types",
          descriptionKey: "command_palette.contract_stakeholder_types_desc",
          icon: mdiAccountGroup,
          category: "settings",
          route: `/${currentOrg}/contract-stakeholder-types`,
        },
        {
          id: "folder-layouts",
          titleKey: "command_palette.folder_layouts",
          descriptionKey: "command_palette.folder_layouts_desc",
          icon: mdiCog,
          category: "settings",
          route: `/${currentOrg}/folder-layouts`,
        },
        {
          id: "organization-settings",
          titleKey: "command_palette.organization_settings",
          descriptionKey: "command_palette.organization_settings_desc",
          icon: SECTION_ICONS.assets,
          category: "settings",
          route: `/${currentOrg}/settings/organization`,
        },
        {
          id: "tags",
          titleKey: "command_palette.tags",
          descriptionKey: "command_palette.tags_desc",
          icon: mdiTag,
          category: "settings",
          route: `/${currentOrg}/settings/tags`,
        },
        {
          // Which AI provider and model run each processing family (#1783).
          id: "ai-models",
          titleKey: "command_palette.ai_models",
          descriptionKey: "command_palette.ai_models_desc",
          icon: mdiAutoFix,
          category: "settings",
          route: `/${currentOrg}/settings/ai-models`,
        },
        {
          id: "derivations",
          titleKey: "command_palette.derivations",
          descriptionKey: "command_palette.derivations_desc",
          icon: mdiSourceBranch,
          category: "settings",
          route: `/${currentOrg}/derivations`,
        },
        {
          id: "normalizations",
          titleKey: "command_palette.normalizations",
          descriptionKey: "command_palette.normalizations_desc",
          icon: mdiSwapHorizontal,
          category: "settings",
          route: `/${currentOrg}/normalizations`,
        },
        {
          id: "aggregations",
          titleKey: "command_palette.aggregation_rules",
          descriptionKey: "command_palette.aggregation_rules_desc",
          icon: mdiCalculator,
          category: "settings",
          route: `/${currentOrg}/aggregations`,
        },
        {
          id: "export",
          titleKey: "command_palette.data_export",
          descriptionKey: "command_palette.data_export_desc",
          icon: mdiDownload,
          category: "navigation",
          route: `/${currentOrg}/data/export`,
        },
        {
          id: "types-export",
          titleKey: "command_palette.types_export",
          descriptionKey: "command_palette.types_export_desc",
          icon: mdiDownload,
          category: "navigation",
          route: `/${currentOrg}/types/export`
        },
        {
          id: "types-import",
          titleKey: "command_palette.types_import",
          descriptionKey: "command_palette.types_import_desc",
          icon: mdiUpload,
          category: "navigation",
          route: `/${currentOrg}/types/import`
        },
        {
          id: "types-sync",
          titleKey: "command_palette.types_sync",
          descriptionKey: "command_palette.types_sync_desc",
          icon: mdiSync,
          category: "navigation",
          route: `/${currentOrg}/types/sync`
        },
        {
          id: "imports",
          titleKey: "command_palette.imports",
          descriptionKey: "command_palette.imports_desc",
          icon: SECTION_ICONS.imports,
          category: "navigation",
          route: `/${currentOrg}/imports`,
        },
        {
          id: "import",
          titleKey: "command_palette.data_import",
          descriptionKey: "command_palette.data_import_desc",
          icon: mdiUpload,
          category: "navigation",
          route: `/${currentOrg}/data/import`,
        },
        {
          id: "compliance-controls",
          titleKey: "command_palette.compliance_controls",
          descriptionKey: "command_palette.compliance_controls_desc",
          icon: mdiShieldCheckOutline,
          category: "settings",
          route: `/${currentOrg}/compliance-controls`,
        },
        {
          id: "compliance-rules",
          titleKey: "command_palette.compliance_rules",
          descriptionKey: "command_palette.compliance_rules_desc",
          icon: mdiScaleBalance,
          category: "settings",
          route: `/${currentOrg}/compliance-rules`,
        },
        {
          id: "property-mapping-rules",
          titleKey: "command_palette.property_mapping_rules",
          descriptionKey: "command_palette.property_mapping_rules_desc",
          icon: mdiSwapHorizontal,
          category: "settings",
          route: `/${currentOrg}/property-mapping-rules`,
        },
        {
          id: "debug",
          titleKey: "command_palette.debug",
          descriptionKey: "command_palette.debug_desc",
          icon: mdiBugOutline,
          category: "navigation",
          route: `/${currentOrg}/debug`,
        }
      )
    }

    return items
  }, [currentOrg])

  // Debounce search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Perform search when debounced query changes
  React.useEffect(() => {
    if (!debouncedQuery.trim() || !currentOrg) {
      setSearchResults([])
      return
    }

    // Only perform search if we're in an organization context
    if (!routeParams?.org && !authOrg) {
      setSearchResults([])
      return
    }

    const performSearch = async () => {
      try {
        // Omit entity_types entirely so the backend searches every indexed
        // entity kind (including contracts and any future kind) rather than
        // a hard-coded subset.
        const queryParams = {
          q: debouncedQuery,
          limit: 5,
        }

        const response = await request(
          '/api/v1/organizations/{orgCode}/search',
          'GET',
          {
            params: {
              path: { orgCode: currentOrg },
              query: queryParams,
            },
          },
        ) as { results: SearchResult[], total: number }

        if (response && response.results) {
          const found = response.results || []
          setSearchResults(found)
          // The search index carries no ancestor chain: resolve it for the
          // asset hits with one list call (`uid[]` + `asset_chain`).
          const assetUids = found.filter(r => r.entity_type === 'asset').map(r => String(r.entity_uid))
          if (assetUids.length > 0) {
            try {
              const chainResponse = await request(
                '/api/v1/organizations/{orgCode}/assets',
                'GET',
                {
                  params: {
                    path: { orgCode: currentOrg },
                    query: { uid: assetUids, asset_chain: true, limit: 50 },
                  },
                },
              ) as { data?: Array<{ uid: string; name?: string | null; parent_asset_uid?: string | null }> }
              const rows = chainResponse?.data ?? []
              const byUid = new Map(rows.map(r => [r.uid, r]))
              const paths: Record<string, string> = {}
              for (const uid of assetUids) {
                const row = byUid.get(uid)
                if (!row) continue
                const names = ancestorChain(row, byUid).map(a => a.name).filter(Boolean)
                if (names.length > 0) paths[uid] = `(${names.join(', ')})`
              }
              setAssetPaths(paths)
            } catch {
              setAssetPaths({})
            }
          } else {
            setAssetPaths({})
          }
        } else {
          setSearchResults([])
        }
      } catch {
        setSearchResults([])
      }
    }

    performSearch()
  }, [debouncedQuery, currentOrg, request, routeParams, authOrg])

  // Filter navigation items based on search query
  const filteredNavigationItems = React.useMemo(() => {
    if (!searchQuery.trim()) return navigationItems

    return navigationItems.filter(item => {
      const title = t(item.titleKey).toLowerCase()
      const description = item.descriptionKey ? t(item.descriptionKey).toLowerCase() : ""
      const query = searchQuery.toLowerCase()
      return title.includes(query) || description.includes(query)
    })
  }, [navigationItems, searchQuery, t])

  const handleSelect = (callback: () => void) => {
    callback()
    onOpenChange(false)
    setSearchQuery("")
  }

  const handleNavigate = (route: string) => {
    navigate({ to: route })
  }

  const handleSearchResultSelect = (result: SearchResult) => {
    let route: string
    switch (result.entity_type) {
      case "asset":
        route = `/${currentOrg}/assets/${result.entity_uid}`
        break
      case "document":
        route = `/${currentOrg}/documents/${result.entity_uid}`
        break
      case "user":
        // Link to /me if the user is the current user, otherwise use the user's UID
        route = user && result.entity_uid === user.uid
          ? `/${currentOrg}/users/me/settings`
          : `/${currentOrg}/users/${result.entity_uid}`
        break
      case "issue":
        route = `/${currentOrg}/issues/${result.entity_uid}`
        break
      case "capex_plan":
        route = `/${currentOrg}/capex-plans/${result.entity_uid}`
        break
      case "contract":
        route = `/${currentOrg}/contracts/${result.entity_uid}`
        break
      case "tag":
        // Selecting a tag from the palette should open the tag-filtered
        // search (the things carrying that tag), not the tag's own settings
        // page — that destination is reserved for the search page's tag
        // rows. Resolving the code is async, so handle it separately.
        if (result.entity_uid && currentOrg) void navigateToTagSearch(currentOrg, result.entity_uid)
        return
      default:
        // Unknown/future entity kind: fall back to the full search page
        // rather than dropping the result silently.
        route = `/${currentOrg}/search?q=${encodeURIComponent(debouncedQuery)}`
        break
    }
    navigate({ to: route })
  }

  // Hands the current query off to the full search page — the destination
  // for both the Cmd/Ctrl+Enter shortcut and the footer button. Carries only
  // `q`; the full page's own filters (entity_types, tags) stay at their
  // defaults, same param shape as navigateToTagSearch below.
  const navigateToFullSearch = (query: string) => {
    if (!currentOrg) return
    navigate({
      to: '/$org/search',
      params: { org: currentOrg },
      search: { q: query.trim(), scope: 'properties', entity_types: undefined, tags: undefined } as { q: string; entity_types: string | undefined; tags: string | undefined; scope: string },
    })
  }

  // Resolves a tag's code from the org's tag catalogue (cached alongside
  // useTags via the same query key) so the search URL carries the code the
  // search page's chip resolution expects — falling back to the uid for a
  // code-less tag or if the catalogue lookup fails.
  const navigateToTagSearch = async (org: string, tagUid: string) => {
    let key = tagUid
    try {
      const tags = await queryClient.fetchQuery({
        queryKey: tagKeys.list(org),
        queryFn: () => fetchTagsList(org),
      })
      const match = tags.find(tag => tag.uid === tagUid)
      if (match?.code) key = match.code
    } catch {
      // Catalogue fetch failed: fall back to the uid, which the backend
      // also accepts for the tags search param.
    }
    navigate({
      to: '/$org/search',
      params: { org },
      // Only `tags` — same minimal shape SearchPage.updateUrl produces for a
      // tag-only filter, so the URL matches what a user would bookmark
      // (`/$org/search?tags=<code>`). Do NOT carry the typed query as `q`:
      // the user typed the tag's *name*, which would needlessly narrow the
      // tagged entities by full-text match.
      search: { tags: key } as { q: string; entity_types: string | undefined; tags: string | undefined; scope: string },
    })
  }

  // Reset search when dialog closes
  React.useEffect(() => {
    if (!open) {
      setSearchQuery("")
      setSearchResults([])
    }
  }, [open])

  // ⌘/Ctrl on macOS vs every other desktop platform.
  const keyHint = isMac() ? "⌘↵" : "Ctrl+↵"

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("command_palette.title")}
      description={t("command_palette.description")}
    >
      <CommandInput
        placeholder={t("command_palette.placeholder")}
        value={searchQuery}
        onValueChange={setSearchQuery}
        onKeyDown={(e) => {
          // Plain Enter keeps its cmdk meaning (select the highlighted
          // item). Cmd/Ctrl+Enter is the app's "submit with modifier"
          // convention (use-send-on-enter.ts, IssueComments, MarkdownEditor)
          // — reused here as the full-search hand-off shortcut. Stop cmdk
          // from also selecting the highlighted row on the same keystroke.
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault()
            e.stopPropagation()
            if (currentOrg) {
              handleSelect(() => navigateToFullSearch(searchQuery))
            }
          }
        }}
      />
      <CommandList>
        <CommandEmpty>
          {loading ? t("command_palette.searching") :
           !currentOrg ? t("command_palette.no_org") :
           t("command_palette.no_results")}
        </CommandEmpty>

        {/* Navigation Items */}
        {filteredNavigationItems.length > 0 && (
          <CommandGroup heading={t("command_palette.group_navigation")}>
            {filteredNavigationItems.map((item) => (
              <CommandItem
                key={item.id}
                onSelect={() => handleSelect(() =>
                  // The static route for "search" carries no query — carry
                  // the typed text at select time so picking it is never
                  // lossy (CommandPalette spec: hand-off to full search).
                  item.id === "search"
                    ? navigateToFullSearch(searchQuery)
                    : handleNavigate(item.route)
                )}
                data-testid={`command-${item.id}`}
              >
                <AppIcon path={item.icon} size="sm" className="mr-2" />
                <div className="flex flex-col">
                  <span>{t(item.titleKey)}</span>
                  {item.descriptionKey && (
                    <span className="text-xs text-muted-foreground">
                      {t(item.descriptionKey)}
                    </span>
                  )}
                </div>
                {item.shortcut && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    {item.shortcut}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Search Results — groups ordered by best score */}
        {searchResults.length > 0 && (() => {
          // Known kinds get a dedicated heading + icon (kept in sync with
          // SearchPage.tsx / the sidebar). Any kind not listed here — e.g. a
          // newly indexed entity type the palette hasn't been taught about
          // yet — falls back to a generic group instead of being dropped.
          const entityConfig: Partial<Record<string, { heading: string; icon: React.ReactNode }>> = {
            asset:      { heading: t("command_palette.assets"),      icon: <AppIcon path={SECTION_ICONS.assets} size="sm" className="mr-2" /> },
            document:   { heading: t("command_palette.documents"),   icon: <AppIcon path={SECTION_ICONS.documents} size="sm" className="mr-2" /> },
            user:       { heading: t("command_palette.users"),       icon: <AppIcon path={mdiAccount} size="sm" className="mr-2" /> },
            issue:      { heading: t("command_palette.issues"),      icon: <AppIcon path={mdiTicketOutline} size="sm" className="mr-2" /> },
            capex_plan: { heading: t("command_palette.capex_plans"), icon: <AppIcon path={SECTION_ICONS.capex} size="sm" className="mr-2" /> },
            contract:   { heading: t("command_palette.contracts"),   icon: <AppIcon path={SECTION_ICONS.contracts} size="sm" className="mr-2" /> },
            tag:        { heading: t("command_palette.tags"),        icon: <AppIcon path={mdiTag} size="sm" className="mr-2" /> },
          }
          const genericConfig = (kind: string): { heading: string; icon: React.ReactNode } => ({
            heading: kind,
            icon: <AppIcon path={mdiMagnify} size="sm" className="mr-2" />,
          })

          // Derive group order from best score per entity type
          const bestScore: Partial<Record<string, number>> = {}
          for (const r of searchResults) {
            const et = r.entity_type
            if (bestScore[et] === undefined || r.score > bestScore[et]!) bestScore[et] = r.score
          }
          const orderedTypes = (Object.keys(bestScore))
            .sort((a, b) => bestScore[b]! - bestScore[a]!)

          return (
            <>
              {orderedTypes.map((entityType) => {
                const config = entityConfig[entityType] ?? genericConfig(entityType)
                const results = searchResults.filter(r => r.entity_type === entityType).slice(0, 5)
                return (
                  <CommandGroup key={entityType} heading={config.heading}>
                    {results.map((result) => (
                      <CommandItem
                        key={`${result.entity_type}-${result.entity_uid}`}
                        onSelect={() => handleSelect(() => handleSearchResultSelect(result))}
                        data-testid={`search-result-${result.entity_uid}`}
                      >
                          {config.icon}
                        <div className="flex flex-col">
                          <span className="flex items-center gap-2">
                            <span
                              dangerouslySetInnerHTML={{
                                __html: result.highlighted_title || result.title
                              }}
                            />
                            {result.entity_type === 'asset' && assetPaths[String(result.entity_uid)] && (
                              <span
                                className="text-xs text-muted-foreground"
                                data-testid={`search-result-path-${result.entity_uid}`}
                              >
                                {assetPaths[String(result.entity_uid)]}
                              </span>
                            )}
                            {result.entity_subtype && (
                              <Badge
                                variant="secondary"
                                className="text-xs"
                                data-testid={`search-result-subtype-${result.entity_uid}`}
                              >
                                {getLocalizedName(result.entity_subtype.name, locale) || result.entity_subtype.code}
                              </Badge>
                            )}
                          </span>
                          {result.description && (
                            <span
                              className="text-xs text-muted-foreground"
                              dangerouslySetInnerHTML={{
                                __html: result.highlighted_description || result.description
                              }}
                            />
                          )}
                        </div>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {t("command_palette.score")}: {formatDisplayNumber(result.score)}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )
              })}
            </>
          )
        })()}
      </CommandList>

      {/* Footer: the palette's permanent "go to full search" affordance.
          Rendered outside CommandList so it survives regardless of which
          CommandEmpty / results state is showing (see spec rationale: a
          pinned CommandItem would steal cmdk's default selection and
          suppress CommandEmpty). */}
      {currentOrg && (
        <div className="border-t px-3 py-2 flex items-center justify-end gap-2 text-xs text-muted-foreground">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleSelect(() => navigateToFullSearch(searchQuery))}
            aria-label={t("command_palette.open_full_search_hint")}
            title={t("command_palette.open_full_search_hint")}
            data-testid="command-open-full-search"
          >
            {t("command_palette.open_full_search")}
            <kbd className="hidden md:inline-block rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
              {keyHint}
            </kbd>
          </Button>
        </div>
      )}
    </CommandDialog>
  )
}
